import COLORS from "./colors";
import { deepFreeze } from "./util/misc";
import Address from "./classes/Address";
import Phone from "./classes/Phone";
import Email from "./classes/Email";

export { COLORS };

export const ORIGIN = "https://app.nwd-ohio.com";

export const SALES_TAX_LOOKUP_URL =
  "https://thefinder.tax.ohio.gov/StreamlineSalesTaxWeb/AddressLookup/LookupByAddress.aspx?taxType=Sales";

export const COMPANY_INFO = {
  nwd: {
    name: "NWD, Inc.",
    shortName: "NWD",
    logo: {
      src: "NWD.svg",
      position: {
        x: -117,
        y: -6,
        height: 85,
      },
      addressPosition: { x: 43, y: 86 },
    },
    get phone() {
      return new Phone({ number: "6148906776", type: "office" });
    },
    get fax() {
      return new Phone({ number: "6148906704", type: "fax" });
    },
    get email() {
      return new Email({ address: "windows@nwd-ohio.com", type: "work" });
    },
  },
  fenestram: {
    name: "Fenestram, LLC.",
    shortName: "Fenestram",
    logo: {
      src: "Fenestram.svg",
      position: {
        x: -295,
        y: 0,
        height: 83,
      },
      addressPosition: { x: 43, y: 86 },
    },
    get phone() {
      return new Phone({ number: "3305764113", type: "office" });
    },
    get email() {
      return new Email({ address: "sales@fenestramohio.com", type: "work" });
    },
  },
  specialty: {
    name: "Specialty North American",
    shortName: "SNA",
    logo: {
      src: "Specialty.svg",
      position: {
        x: -9,
        y: -2,
        height: 92,
      },
      addressPosition: { x: 37, y: 90 },
    },
    get phone() {
      return new Phone({ number: "6145057336", type: "office" });
    },
    get email() {
      return new Email({ address: "service@snaohio.com", type: "work" });
    },
  },
};

const LOCATION_COMPANY_CONTACT_FIELDS = ["phone", "fax", "email"];

function mergeLocationCompanyInfo(location) {
  if (!location || !location.company) {
    return null;
  }

  const company = { ...location.company };
  LOCATION_COMPANY_CONTACT_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(location, field)) {
      company[field] = location[field];
    }
  });

  return company;
}

export function getLocationInfo(location) {
  if (!location) {
    return null;
  }
  if (typeof location === "string") {
    return LOCATION_INFO[location] || null;
  }
  return location;
}

export function getLocationCompany(location) {
  const locationInfo = getLocationInfo(location);
  return mergeLocationCompanyInfo(locationInfo);
}

export const LOCATIONS = [
  { label: "Bonita Springs, FL", value: "bonitasprings" },
  { label: "Cincinnati, OH", value: "cincinnati" },
  { label: "Medina, OH", value: "medina" },
  { label: "Plain City, OH", value: "plaincity" },
  { label: "Westerville, OH - NWD", value: "westerville" },
  { label: "Westerville, OH - SNA", value: "worthington" },
  { label: "Westerville, OH - Warehouse", value: "westervillewh" },
];

// Keep the addresses in sync with WAREHOUSE_ADDRESSES / PICKUP_COUNTERS in
// functions/pickupLocations.js. The warehouses at each location come from the
// warehouseMaps collection (`locationId`), not here.
export const LOCATION_INFO = {
  westerville: {
    company: COMPANY_INFO.nwd,
    get address() {
      return new Address({
        street: "893 Eastwind Dr.",
        city: "Westerville",
        state: "OH",
        zip: "43081",
        country: "US",
        type: "work",
        lat: 40.1013217162639,
        lng: -82.91483510614476,
      });
    },
    timeZone: "America/New_York",
  },
  worthington: {
    company: COMPANY_INFO.specialty,
    get address() {
      return new Address({
        street: "893 Eastwind Dr.",
        city: "Westerville",
        state: "OH",
        zip: "43081",
        country: "US",
        type: "work",
        lat: 40.1013217162639,
        lng: -82.91483510614476,
      });
    },
    timeZone: "America/New_York",
  },
  westervillewh: {
    company: COMPANY_INFO.nwd,
    get address() {
      return new Address({
        street: "180 E Broadway Ave",
        city: "Westerville",
        state: "OH",
        zip: "43081",
        country: "US",
        type: "work",
        lat: 40.13042268251326,
        lng: -82.92555668827532,
      });
    },
    timeZone: "America/New_York",
  },
  plaincity: {
    company: COMPANY_INFO.nwd,
    get address() {
      return new Address({
        street: "2740 Rosedale-Plain City Rd.",
        city: "Plain City",
        state: "OH",
        zip: "43064",
        country: "US",
        type: "work",
        lat: 40.0887533385879,
        lng: -83.35867562708815,
      });
    },
    timeZone: "America/New_York",
  },
  medina: {
    company: COMPANY_INFO.fenestram,
    get address() {
      return new Address({
        street: "1113 Medina Rd., Suite 550",
        city: "Medina",
        state: "OH",
        zip: "44256",
        country: "US",
        type: "work",
        lat: 41.13758530494966,
        lng: -81.72757279297373,
      });
    },
    timeZone: "America/New_York",
  },
  cincinnati: {
    company: COMPANY_INFO.specialty,
    get address() {
      return new Address({
        street: "8180 Montgomery Rd",
        city: "Cincinnati",
        state: "OH",
        zip: "45236",
        country: "US",
        type: "work",
        lat: 39.20442485572327,
        lng: -84.36787540301047,
      });
    },
    timeZone: "America/New_York",
  },
  bonitasprings: {
    company: COMPANY_INFO.fenestram,
    get address() {
      return new Address({
        street: "24850 Old 41 Road",
        city: "Bonita Springs",
        state: "FL",
        zip: "34135",
        country: "US",
        type: "work",
      });
    },
    get phone() {
      return new Phone({ number: "2398340663", type: "office" });
    },
    get email() {
      return new Email({ address: "sales@fenestramflorida.com", type: "work" });
    },
  },
};

LOCATIONS.forEach((location) => {
  const info = LOCATION_INFO[location.value];
  info.color = COLORS[location.value] || null;
  Object.assign(info, location);
  info.company = mergeLocationCompanyInfo(info);
  location.company = info.company.name;
});
deepFreeze(LOCATIONS);
